/**
 * Shared `sx` constants for the sidebar nav. Extracted from DrawerContent so
 * that file stays within the 250-line limit as the nav grows.
 *
 * Colours come from the `v4` palette (see `configs/themes.ts`) and padding from
 * theme spacing units (8px base), so nothing here is a magic literal. Font and
 * border widths stay in px — they are not on the spacing scale.
 */

/**
 * Baseline left border, as separate longhands rather than the `borderLeft`
 * shorthand. Object spread doesn't reposition a key that already exists
 * earlier in the object — reassigning it only updates the value in place —
 * so once a consumer's base styles (e.g. navItemBaseSx) already declare
 * `borderLeft*`, a *later* spread of `border: 0` (a same-object shorthand
 * reset for all four sides, incl. style/color) still lands after them in the
 * final object and wins the cascade, silently flattening the border back to
 * invisible. That's what was breaking the accent stripe on nested group
 * headers — buttonResetSx's `border: 0` was output after navItemBaseSx's
 * border-left declaration regardless of source order. Splitting the reset so
 * nothing but this exact key ever controls border-left-color removes the
 * shorthand collision entirely: there's only one key of this name anywhere
 * in the merge chain, so last-spread-wins is unambiguous and position-proof.
 */
export const borderLeftBaseSx = {
  borderLeftWidth: '3px',
  borderLeftStyle: 'solid',
  borderLeftColor: 'transparent',
} as const;

/**
 * Reset applied to the collapsible headers, which render as `ButtonBase` (a
 * real `<button>`) so keyboard activation and semantics come from the platform.
 * ButtonBase centres its content and drops the inherited font, so pin both back.
 * Resets the other three sides via their shorthand (nothing else ever styles
 * them, so no collision) and owns the left-border baseline itself as pure
 * longhands — see borderLeftBaseSx above for why it can't just be `border: 0`.
 */
const buttonResetSx = {
  width: '100%',
  justifyContent: 'flex-start',
  textAlign: 'left',
  fontFamily: 'inherit',
  borderTop: 0,
  borderRight: 0,
  borderBottom: 0,
  ...borderLeftBaseSx,
  // ButtonBase sets `outline: 0` and we disable the ripple, so without this the
  // headers would have NO visible keyboard focus indicator (WCAG 2.4.7).
  // `currentColor` tracks the label colour, so the ring stays legible against
  // the sidebar in both the default and hovered/active states.
  '&.Mui-focusVisible': {
    outline: '2px solid currentColor',
    outlineOffset: '-2px',
  },
} as const;

const hoverSx = { bgcolor: 'v4.sidebarActive', color: 'common.white' } as const;

/** Top-level section row (collapsible header, or a standalone link like Home). */
export const sectionHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  py: 1,
  px: 1.75,
  fontSize: '14px',
  fontWeight: 600,
  color: 'v4.sidebarText',
  cursor: 'pointer',
  textDecoration: 'none',
  transition: 'background .15s',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  '&:hover': hoverSx,
} as const;

/** Top-level section header rendered as a button. */
export const sectionHeaderButtonSx = {
  ...sectionHeaderSx,
  ...buttonResetSx,
} as const;

/** Second-level nav link. */
export const navItemBaseSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  py: 0.75,
  px: 1.75,
  pl: 3.75,
  ...borderLeftBaseSx,
  fontSize: '14px',
  color: 'v4.sidebarText',
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'background .15s',
  whiteSpace: 'nowrap',
  '&:hover': hoverSx,
} as const;

/**
 * Second-level collapsible group header (e.g. Jobs > Quick Views). Styled as a
 * nav row rather than a caption because it is an interactive control.
 */
export const groupHeaderSx = {
  ...navItemBaseSx,
  ...buttonResetSx,
  userSelect: 'none',
} as const;

/**
 * Third-level nav link, indented past its group header so the hierarchy is
 * legible — depth-2 and depth-3 items previously shared the same indent.
 */
export const nestedNavItemSx = {
  ...navItemBaseSx,
  pl: 5.5,
} as const;

export const activeItemSx = {
  bgcolor: 'v4.sidebarActive',
  borderLeftColor: 'v4.accent',
  color: 'common.white',
} as const;

export const iconSx = { fontSize: 20 } as const;

export const chevronSx = (isExpanded: boolean) =>
  ({
    fontSize: 16,
    color: 'v4.sidebarTextMuted',
    ml: 'auto',
    transition: 'transform 0.2s ease',
    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
  }) as const;
